import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarCheck, MapPin, MessageCircle, Navigation, Phone, User, Wrench } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { fetchEvent } from "@/lib/api";
import { BRANCHES, fmtDate, mapLink, phoneLink, waLink } from "@/lib/format";

const Step = ({ icon: Icon, color, label, date, time, testId }) => (
  <div data-testid={testId} className="relative pl-10">
    <span className={`absolute left-0 top-0 grid h-7 w-7 place-items-center rounded-sm ${color}`}><Icon size={14} /></span>
    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-display text-2xl font-bold capitalize">{fmtDate(date, "EEEE, dd/MM/yyyy")}</div>
    <div className="font-mono text-sm text-amber-400">{time || "Horário a definir"}</div>
  </div>
);

export default function EventDetailPage() {
  const { id } = useParams();
  const [ev, setEv] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchEvent(id).then(setEv).catch(() => setError(true));
  }, [id]);

  if (error) return <main className="mx-auto max-w-4xl px-4 py-20 text-center" data-testid="event-not-found"><p className="text-slate-400">Evento não encontrado.</p><Link to="/" className="mt-4 inline-block text-amber-400">Voltar à agenda</Link></main>;
  if (!ev) return <main className="mx-auto max-w-4xl px-4 py-20"><div className="h-80 animate-pulse rounded-md bg-slate-900/60" /></main>;

  return (
    <main data-testid="event-detail-page" className="relative z-10 mx-auto max-w-6xl px-4 pb-24 pt-8 sm:px-8">
      <Link to="/" data-testid="back-to-agenda-link" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-amber-400"><ArrowLeft size={16} />Voltar à agenda</Link>
      <header className="rise mb-10 space-y-4">
        <div className="flex flex-wrap items-center gap-3"><StatusBadge status={ev.status} testId="event-detail-status" /><span data-testid="event-detail-branch" className="rounded-sm border border-slate-700 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-300">Filial {BRANCHES[ev.branch]}</span>{ev.client && <span className="text-sm text-slate-500">{ev.client}</span>}</div>
        <h1 data-testid="event-detail-title" className="font-display text-4xl font-extrabold uppercase leading-none sm:text-5xl lg:text-6xl">{ev.title}</h1>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <section className="rise space-y-6 rounded-md border border-slate-800/80 bg-slate-900/60 p-6" style={{ animationDelay: "80ms" }}>
          <h2 className="font-mono text-xs uppercase tracking-widest text-slate-500">Cronograma</h2>
          <div className="space-y-6 border-l border-dashed border-slate-700 pl-0 [&>*]:-ml-3.5">
            <Step icon={Wrench} color="bg-emerald-500 text-slate-950" label="Montagem do equipamento" date={ev.setup_date} time={ev.setup_time} testId="event-setup-step" />
            <Step icon={CalendarCheck} color="bg-amber-500 text-white" label="Data do evento" date={ev.event_date} time={ev.event_time} testId="event-date-step" />
            <Step icon={Wrench} color="bg-indigo-500 text-white" label="Desmontagem do equipamento" date={ev.teardown_date} time={ev.teardown_time} testId="event-teardown-step" />
          </div>
        </section>

        <div className="space-y-6">
          <section className="rise space-y-3 rounded-md border border-slate-800/80 bg-slate-900/60 p-6" style={{ animationDelay: "140ms" }}>
            <h2 className="font-mono text-xs uppercase tracking-widest text-slate-500">Local</h2>
            <p data-testid="event-location" className="flex items-start gap-2 font-display text-2xl font-bold"><MapPin className="mt-1 shrink-0 text-amber-400" size={20} />{ev.location}</p>
            {ev.address && <p data-testid="event-address" className="text-sm text-slate-400">{ev.address}</p>}
            <a href={mapLink(ev)} target="_blank" rel="noreferrer" data-testid="event-map-link" className="inline-flex items-center gap-2 rounded-sm bg-amber-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-400"><Navigation size={15} />Abrir no mapa</a>
          </section>

          {(ev.responsible_name || ev.responsible_phone) && (
            <section className="rise space-y-3 rounded-md border border-slate-800/80 bg-slate-900/60 p-6" style={{ animationDelay: "200ms" }}>
              <h2 className="font-mono text-xs uppercase tracking-widest text-slate-500">Responsável</h2>
              <p data-testid="event-responsible" className="flex items-center gap-2 text-lg font-medium"><User size={18} className="text-amber-400" />{ev.responsible_name || "—"}</p>
              {ev.responsible_phone && (
                <div className="flex flex-wrap gap-2">
                  <a href={phoneLink(ev.responsible_phone)} data-testid="event-contact-button" className="inline-flex items-center gap-2 rounded-sm border border-slate-700 px-4 py-2 text-sm transition-colors hover:border-amber-500"><Phone size={15} />{ev.responsible_phone}</a>
                  <a href={waLink(ev.responsible_phone)} target="_blank" rel="noreferrer" data-testid="event-whatsapp-button" className="inline-flex items-center gap-2 rounded-sm border border-emerald-600/50 px-4 py-2 text-sm text-emerald-300 transition-colors hover:bg-emerald-500/10"><MessageCircle size={15} />WhatsApp</a>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      <section className="rise mt-6 rounded-md border border-slate-800/80 bg-slate-900/60 p-6" style={{ animationDelay: "260ms" }}>
        <h2 className="mb-4 font-mono text-xs uppercase tracking-widest text-slate-500">Equipamentos locados</h2>
        {ev.equipment.length === 0 ? <p className="text-sm text-slate-500">Nenhum equipamento cadastrado.</p> : (
          <ul data-testid="event-equipment-list" className="divide-y divide-slate-800">
            {ev.equipment.map((e, i) => (
              <li key={i} data-testid={`event-equipment-item-${i}`} className="flex items-center justify-between py-3">
                <span>{e.name}</span><span className="font-mono text-amber-400">× {e.quantity}</span>
              </li>
            ))}
          </ul>
        )}
        {ev.notes && <p data-testid="event-notes" className="mt-6 whitespace-pre-line border-t border-slate-800 pt-4 text-sm text-slate-400">{ev.notes}</p>}
      </section>
    </main>
  );
}
